import type { FileAccessLevel } from "@/lib/api";
import type { ChatMessageDto, ToolUseContentBlock } from "@/lib/chat";
import { isFileAccessLevel } from "@/lib/file-policy-rules";

const TOOL_APPROVAL_PROTOCOL_VERSION = 1 as const;

export type ToolApprovalStatus =
	| "pending"
	| "approved"
	| "denied"
	| "executing"
	| "executed"
	| "cancelled"
	| "interrupted";

export interface ToolApprovalManifest {
	fileCount: number;
	totalBytes: number;
	digest: string;
}

export interface ToolApprovalRuleSnapshot {
	action: "deny" | "ask" | "allow";
	toolName: string | null;
	argPattern: string | null;
}

export interface ToolApprovalFileRuleSnapshot {
	access: FileAccessLevel;
	pattern: string;
}

export interface ToolApprovalMarker {
	type: "tool_approval";
	version: 1;
	event: "requested" | "resolved";
	toolUseId: string;
	category: string;
	status: ToolApprovalStatus;
	manifest?: ToolApprovalManifest;
	matchedRule?: ToolApprovalRuleSnapshot;
	matchedFileRule?: ToolApprovalFileRuleSnapshot;
	workingDirectory?: string;
}

export interface PendingToolApproval {
	marker: ToolApprovalMarker;
	toolUse: ToolUseContentBlock;
}

const REQUESTED_STATUSES = new Set<ToolApprovalStatus>(["pending"]);
const RESOLVED_STATUSES = new Set<ToolApprovalStatus>([
	"approved",
	"denied",
	"executing",
	"executed",
	"cancelled",
	"interrupted",
]);

/** v1 の approval marker を厳密に読み取る。壊れた値や将来 version は通常テキストとして扱う。 */
export function parseToolApprovalMarker(text: string): ToolApprovalMarker | null {
	let parsed: unknown;
	try {
		parsed = JSON.parse(text);
	} catch {
		return null;
	}
	if (typeof parsed !== "object" || parsed === null) return null;
	const value = parsed as Record<string, unknown>;
	if (
		value.type !== "tool_approval" ||
		value.version !== TOOL_APPROVAL_PROTOCOL_VERSION ||
		(value.event !== "requested" && value.event !== "resolved") ||
		typeof value.toolUseId !== "string" ||
		value.toolUseId.length === 0 ||
		typeof value.category !== "string" ||
		value.category.length === 0 ||
		typeof value.status !== "string"
	) {
		return null;
	}
	const status = value.status as ToolApprovalStatus;
	if (
		(value.event === "requested" && !REQUESTED_STATUSES.has(status)) ||
		(value.event === "resolved" && !RESOLVED_STATUSES.has(status))
	) {
		return null;
	}
	const manifestValue = value.manifest;
	let manifest: ToolApprovalManifest | undefined;
	if (manifestValue !== undefined && manifestValue !== null) {
		if (typeof manifestValue !== "object" || manifestValue === null) return null;
		const candidate = manifestValue as Record<string, unknown>;
		if (
			typeof candidate.fileCount !== "number" ||
			!Number.isSafeInteger(candidate.fileCount) ||
			candidate.fileCount < 0 ||
			typeof candidate.totalBytes !== "number" ||
			!Number.isSafeInteger(candidate.totalBytes) ||
			candidate.totalBytes < 0 ||
			typeof candidate.digest !== "string" ||
			candidate.digest.length === 0
		) {
			return null;
		}
		manifest = {
			fileCount: candidate.fileCount,
			totalBytes: candidate.totalBytes,
			digest: candidate.digest,
		};
	}
	const matchedRuleValue = value.matchedRule;
	let matchedRule: ToolApprovalRuleSnapshot | undefined;
	if (matchedRuleValue !== undefined && matchedRuleValue !== null) {
		if (typeof matchedRuleValue !== "object" || matchedRuleValue === null) return null;
		const candidate = matchedRuleValue as Record<string, unknown>;
		if (
			(candidate.action !== "deny" && candidate.action !== "ask" && candidate.action !== "allow") ||
			(candidate.toolName !== null && typeof candidate.toolName !== "string") ||
			(candidate.argPattern !== null && typeof candidate.argPattern !== "string")
		) {
			return null;
		}
		matchedRule = {
			action: candidate.action,
			toolName: candidate.toolName,
			argPattern: candidate.argPattern,
		};
	}
	const matchedFileRuleValue = value.matchedFileRule;
	let matchedFileRule: ToolApprovalFileRuleSnapshot | undefined;
	if (matchedFileRuleValue !== undefined && matchedFileRuleValue !== null) {
		if (typeof matchedFileRuleValue !== "object") return null;
		const candidate = matchedFileRuleValue as Record<string, unknown>;
		if (
			!isFileAccessLevel(candidate.access) ||
			typeof candidate.pattern !== "string" ||
			candidate.pattern.length === 0
		) {
			return null;
		}
		matchedFileRule = {
			access: candidate.access,
			pattern: candidate.pattern,
		};
	}
	const workingDirectoryValue = value.workingDirectory;
	if (
		workingDirectoryValue !== undefined &&
		workingDirectoryValue !== null &&
		typeof workingDirectoryValue !== "string"
	) {
		return null;
	}
	return {
		type: "tool_approval",
		version: TOOL_APPROVAL_PROTOCOL_VERSION,
		event: value.event,
		toolUseId: value.toolUseId,
		category: value.category,
		status,
		...(manifest === undefined ? {} : { manifest }),
		...(matchedRule === undefined ? {} : { matchedRule }),
		...(matchedFileRule === undefined ? {} : { matchedFileRule }),
		...(typeof workingDirectoryValue === "string"
			? { workingDirectory: workingDirectoryValue }
			: {}),
	};
}

/** request / resolution marker を時系列で畳み、tool_use ごとの最新承認状態を返す。 */
function foldToolApprovalMarkers(
	messages: readonly ChatMessageDto[],
): ReadonlyMap<string, ToolApprovalMarker> {
	const markers = new Map<string, ToolApprovalMarker>();
	for (const message of messages) {
		if (message.role !== "systemNotice") continue;
		for (const block of message.content) {
			if (block.type !== "text") continue;
			const marker = parseToolApprovalMarker(block.text);
			if (marker !== null) markers.set(marker.toolUseId, marker);
		}
	}
	return markers;
}

/**
 * 履歴の request/resolution marker を時系列で畳み、現在の未決承認を返す。
 * 実行引数は marker の複製ではなく、必ず assistant tool_use を正とする。
 */
export function findPendingToolApproval(
	messages: readonly ChatMessageDto[],
): PendingToolApproval | null {
	const toolUses = new Map<string, ToolUseContentBlock>();
	for (const message of messages) {
		if (message.role !== "assistant") continue;
		for (const block of message.content) {
			if (block.type === "tool_use") toolUses.set(block.id, block);
		}
	}

	const last = Array.from(foldToolApprovalMarkers(messages).values()).findLast(
		(marker) => marker.status === "pending",
	);
	if (last === undefined) return null;
	const toolUse = toolUses.get(last.toolUseId);
	return toolUse === undefined ? null : { marker: last, toolUse };
}
